import type { Bytes } from "@pdfking/tfunc"
import type { Allocated } from "./Allocated"
import type { Pointer } from "./Pointer"

/** Generic WASM instance with memory management helpers */
export type WasmInstance = {
  /** Raw WASM exports object (use for module-specific function calls) */
  readonly exports: Record<string, unknown>
  /** Allocate `size` bytes, returns pointer + free function */
  readonly malloc: (size: Bytes) => Allocated
  /** Write bytes at pointer */
  readonly writeTo: (ptr: Pointer, data: Uint8Array) => void
  /** Allocate and write bytes, returns pointer + free function */
  readonly writeBytes: (data: Uint8Array) => Allocated
  /** Allocate and write a null-terminated string, returns pointer + free function */
  readonly writeString: (str: string) => Allocated
  /** Read `size` bytes from pointer (returns a copy) */
  readonly readBytes: (ptr: Pointer, size: Bytes) => Uint8Array
  /** Read float32 values from pointer */
  readonly readFloats: (ptr: Pointer, count: number) => Float32Array
  /** Write an int32 at pointer (little-endian) */
  readonly setInt32: (ptr: Pointer, value: number) => void
  /** Register a JS callback in the indirect function table */
  readonly addCallback: (
    fn: (...args: number[]) => number,
    sig: string
  ) => Allocated
  /** Current WASM memory size */
  readonly getMemorySize: () => Bytes
  /** Re-initialize the instance (re-instantiate from compiled module) */
  readonly init: () => Promise<void>
  /** Dispose all exports (cleanup) */
  readonly dispose: () => Promise<void>
}
